import { Component, Input, OnInit } from '@angular/core';
import { ElementAttribute } from '@models/editor';
import { CanvasInputMode } from '@service/canvas/canvas-input-mode.service';
import { CanvasService } from '@service/canvas/canvas.service';
import { ElementOrientationService } from '@service/canvas/element-orientation.service';
import { GaugeExtensionService } from '@service/canvas/gauge-extension.service';

interface DefaultItem {
    svgIcon?: string;
    imgSrc?: string;
    name: string;
    circle?: boolean;
    type?: CanvasInputMode;
}

const DEFAULT_CATEGORY_ITEMS: Record<string, DefaultItem[]> = {
    'default-valves': [
        {
            svgIcon: 'valve-hori-static-2-way-static-horizontal-no-indicator',
            name: 'mimic.library.default-valve',
            circle: true,
            type: CanvasInputMode.DEFAULT_VALVE,
        },
    ],
    'default-tanks': [
        {
            svgIcon: 'tank-default',
            name: 'mimic.library.default',
            type: CanvasInputMode.DEFAULT_TANK,
        },
        {
            svgIcon: 'tank-main-air-reserve',
            name: 'mimic.library.main-air-reserve',
            type: CanvasInputMode.DEFAULT_TANK,
        },
        {
            svgIcon: 'tank-auxiliary-air-reserve',
            name: 'mimic.library.auxiliary-air-reserve',
            type: CanvasInputMode.DEFAULT_TANK,
        },
    ],
    'default-pumps': [
        {
            svgIcon: 'pump-vertical-right',
            name: 'mimic.library.default-pump',
            circle: true,
            type: CanvasInputMode.DEFAULT_PUMP,
        },
    ],
    'default-fans': [
        {
            svgIcon: 'fan-stop',
            name: 'mimic.library.default-fan',
            circle: true,
            type: CanvasInputMode.DEFAULT_FAN,
        },
    ],
    'default-switch': [
        {
            svgIcon: 'switch-off',
            name: 'mimic.library.default-switch',
            type: CanvasInputMode.DEFAULT_SWITCH,
        },
    ],
    'default-gauge': [
        {
            svgIcon: 'gauge-default',
            name: 'mimic.library.default-gauge',
            type: CanvasInputMode.GAUGE,
        },
    ],
};

@Component({
    selector: 'app-default-component-dialog',
    templateUrl: './default-component-dialog.component.html',
    styleUrls: ['./default-component-dialog.component.css'],
    standalone: false,
})
export class DefaultComponentDialogComponent implements OnInit {
    @Input() x: number = 0;
    @Input() y: number = 0;
    @Input() selectedCategoryId: string = '';

    canvas: any;

    constructor(
        private canvasService: CanvasService,
        private elementOrientationService: ElementOrientationService,
        private gaugeExtensionService: GaugeExtensionService
    ) {}

    ngOnInit(): void {
        this.canvas = this.canvasService.getCanvas();
    }

    get items(): DefaultItem[] {
        return DEFAULT_CATEGORY_ITEMS[this.selectedCategoryId] ?? [];
    }

    addToCanvas(event: MouseEvent, item: DefaultItem): void {
        const container = event.currentTarget as HTMLElement;
        const svg = container.querySelector('svg') as SVGSVGElement | null;
        if (!svg) return;

        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');

        if (item.circle) {
            const vb = svg.viewBox.baseVal;
            const cx = vb.x + vb.width / 2;
            const cy = vb.y + vb.height / 2;
            const r = Math.max(vb.width, vb.height) / 2;

            const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            circle.setAttribute(ElementAttribute.id, this.canvas.getNextId());
            circle.setAttribute(ElementAttribute.cx, String(cx));
            circle.setAttribute(ElementAttribute.cy, String(cy));
            circle.setAttribute('r', String(r));
            circle.setAttribute(ElementAttribute.fill, '#ffffff');
            circle.setAttribute(ElementAttribute.stroke, '#2c3a48');
            circle.setAttribute(ElementAttribute.strokeWidth, '1.5');
            g.appendChild(circle);
        }

        Array.from(svg.children).forEach(originalChild => {
            const computed = window.getComputedStyle(originalChild);
            const clone = originalChild.cloneNode(true) as SVGElement;
            clone.setAttribute('fill', computed.fill);
            clone.setAttribute('stroke', computed.stroke);
            g.appendChild(clone);
        });

        const groupId = this.canvas.getNextId();
        g.setAttribute(ElementAttribute.id, groupId);
        g.setAttribute(ElementAttribute.resizable, 'true');
        if (item.type) {
            g.setAttribute(ElementAttribute.iCreateType, item.type);
        }
        if (item.svgIcon && item.type === CanvasInputMode.DEFAULT_PUMP) {
            g.setAttribute(ElementAttribute.pumpIcon, item.svgIcon);
        }

        g.childNodes.forEach((child: any) => {
            if (!child.getAttribute(ElementAttribute.id)) {
                child.setAttribute(ElementAttribute.id, this.canvas.getNextId());
            }
        });

        const svgRoot = this.canvasService.getSvgRoot() as unknown as HTMLElement;
        const layer = svgRoot.querySelector('.layer');
        layer.appendChild(g);

        const bbox = (g as any).getBBox();
        const centerX = this.canvas.contentW / 2;
        const centerY = this.canvas.contentH / 2;
        const dx = centerX - (bbox.x + bbox.width / 2);
        const dy = centerY - (bbox.y + bbox.height / 2);
        g.setAttribute(ElementAttribute.transform, `translate(${dx}, ${dy})`);
        this.canvas.recalculateDimensions(g);
        this.canvas.addCommandToHistory(new this.canvas.history.InsertElementCommand(g));
        this.canvas.call('changed', [g]);
        this.canvas.selectOnly([g]);

        if (item.type === CanvasInputMode.DEFAULT_VALVE) {
            this.elementOrientationService.applyValveOrientation(g as SVGGElement, 'horizontal');
        }

        if (item.type === CanvasInputMode.GAUGE) {
            g.setAttribute(
                ElementAttribute.transform,
                'matrix(0.9999999237060605, 0, 0, 0.9999999237060605, 0.00006890582756113872, 0.000026302811818368355)'
            );
            this.gaugeExtensionService.render(g as SVGGElement);
        }
    }
}
